import { setIn } from '../../helpers/helpers';
import { getFieldKeyPathById } from '../selectors';
import { TemplateEditorPayload, TemplateState } from '../templateeditor-types';
import { addFieldPositionReturnResult, updateFieldName } from '../updaters';
import { AiTemplateBuilderPayload, DetectedField, FieldInputType, FieldSuggestion } from './templateeditor-ai-types';

export function applyFieldSuggestions(state: TemplateState, payload: AiTemplateBuilderPayload): TemplateState {
  const { fieldSuggestion } = payload;
  const { field } = fieldSuggestion;

  const fieldId = placeFieldOnTemplate(state, fieldSuggestion);
  applySuggestedFieldName(state, fieldId, field);

  return state;
}

// PRIVATES

function placeFieldOnTemplate(state: TemplateState, fieldSuggestion: FieldSuggestion): string {
  const { field, features, rawDateFormat, pageKeyPath } = fieldSuggestion;
  const { x, y, width } = field.fieldBoundingBox;
  const { fieldInputType } = field;

  const height = field.fieldBoundingBox.height;

  const addFieldPositionPayload: TemplateEditorPayload = {
    pageKeyPath,
    type: fieldInputType,
    x,
    y,
    width,
    height,
    rawDateFormat,
    features,
    checkBoxGroupItemId: undefined,
    field: undefined,
    tableFieldProperties: {
      rowCount: field.tableFieldProperties?.rowCount,
      columns: field.tableFieldProperties?.columns?.map((column) => ({
        name: column.label,
        type: column.fieldInputType,
        width: column.width,
        transparent: column.isTransparent
      }))
    },
    // to handle the case where a field is placed on the template by ai, mark it as field committed so
    // we don't show the placeholder message
    isFieldCommitted: true,
    isAiPlaced: true
  };

  const { fieldId } = addFieldPositionReturnResult(state, addFieldPositionPayload);

  return fieldId;
}

function applySuggestedFieldName(state: TemplateState, fieldId: string, field: DetectedField) {
  // with the keypath of the newly added field, apply the field name
  // look through the state to see if there are any fields with that name already, if there are, append a number to the end
  // replace all trailing non-alphanumeric characters

  const fieldKeyPath = getFieldKeyPathById(state, fieldId);
  const getTemplateSafeName = (label: string) => {
    const suggestedName = label.replace(/[^\w\s]+$/g, '');
    // todo - could improve this by selecting this into a map of field names and counts
    const existingFieldNameCount = state.template?.fields?.filter((f) => simpleDuplicateNameCheck(f.name, suggestedName)).length || 0;
    const fieldName = existingFieldNameCount === 0 ? suggestedName : `${suggestedName} ${existingFieldNameCount + 1}`;
    return fieldName;
  };

  const templateSafeFieldName = getTemplateSafeName(field.label);
  updateFieldName(state, { keyPath: [...fieldKeyPath, 'name'], value: templateSafeFieldName });

  // if this is a table then we have to go through and name the columns as well
  if (field.fieldInputType == FieldInputType.Table) {
    field.tableFieldProperties?.columns?.forEach((column, index) => {
      const columnsKeyPath = [...fieldKeyPath, 'columns', index];
      const templateSafeColumnName = getTemplateSafeName(column.label);

      setIn(state, [...columnsKeyPath, 'name'], templateSafeColumnName);
    });
  }
}

// todo - consider moving this to a helper and using it in the main template updater
function simpleDuplicateNameCheck(existingName: string, newName: string): boolean {
  if (!existingName || !newName) {
    return false;
  }

  const loweredExisting = existingName.toLowerCase();
  const loweredNewName = newName.toLowerCase();

  if (loweredExisting === loweredNewName) {
    return true;
  }

  // strip out any numbers at the end of the source and compare
  if (loweredExisting.replace(/\d+$/, '').trim() === loweredNewName.replace(/\d+$/, '').trim()) {
    return true;
  }

  return false;
}
